import { useState, useEffect } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import api from "../../../api";
import { Loader2, Award, Lock, Trophy } from "lucide-react";

export default function Achievements() {
    const [allAchievements, setAllAchievements] = useState([]);
    const [myAchievements, setMyAchievements] = useState([]);
    const [stats, setStats] = useState({ total_xp: 0, level: 1, next_level_xp: 100 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [allRes, myRes] = await Promise.all([
                api.getAchievements(),
                api.getMyAchievements(),
            ]);
            setAllAchievements(allRes.data || []);
            // Handle new structure { achievements: [], stats: {} }
            if (myRes.data && myRes.data.achievements) {
                setMyAchievements(myRes.data.achievements);
                setStats(myRes.data.stats);
            } else {
                // Fallback for old structure
                setMyAchievements(myRes.data || []);
            }
        } catch (err) {
            console.error("Error fetching achievements", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex h-screen items-center justify-center">
                    <Loader2 className="animate-spin text-blue-600" size={48} />
                </div>
            </DashboardLayout>
        );
    }

    // Helper to check unlock status
    const isUnlocked = (achId) => myAchievements.some((ua) => ua.achievement.id === achId);
    const getUnlockDate = (achId) => {
        const ua = myAchievements.find((ua) => ua.achievement.id === achId);
        return ua ? new Date(ua.earned_at).toLocaleDateString() : null;
    };

    // Group by category
    const categories = ["session", "streak", "performance", "learning", "general"];

    return (
        <DashboardLayout>
            <div className="max-w-6xl mx-auto p-6">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold dark:text-white">Achievements</h1>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">
                            Level {stats.level} Therapist • {stats.total_xp} XP Total
                        </p>
                        {/* Progress Bar */}
                        <div className="w-64 mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-500 rounded-full"
                                style={{ width: `${(stats.total_xp % 100)}%` }} // Simplified percentage logic
                            ></div>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{stats.next_level_xp - stats.total_xp} XP to Level {stats.level + 1}</p>
                    </div>
                    <div className="bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 p-4 rounded-xl border border-yellow-200 dark:border-yellow-700/50 flex items-center gap-4">
                        <div className="bg-yellow-500 rounded-full p-2 text-white shadow-lg shadow-yellow-500/30">
                            <Trophy size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-100 uppercase tracking-wider">
                                Unlocked
                            </p>
                            <p className="text-3xl font-bold text-yellow-900 dark:text-white">
                                {myAchievements.length} <span className="text-sm font-normal text-yellow-700 dark:text-yellow-200">/ {allAchievements.length}</span>
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {allAchievements.map((ach) => {
                        const unlocked = isUnlocked(ach.id);
                        return (
                            <div
                                key={ach.id}
                                className={`
                  relative overflow-hidden rounded-xl border p-6 transition-all duration-300
                  ${unlocked
                                        ? "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md"
                                        : "bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800 opacity-70 grayscale"
                                    }
                `}
                            >
                                <div className="flex items-start justify-between">
                                    <div className={`p-3 rounded-lg ${unlocked ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400" : "bg-gray-200 dark:bg-gray-800 text-gray-400"}`}>
                                        <Award size={28} />
                                    </div>
                                    {unlocked ? (
                                        <span className="text-xs font-medium text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-full">
                                            Unlocked {getUnlockDate(ach.id)}
                                        </span>
                                    ) : (
                                        <Lock size={16} className="text-gray-400" />
                                    )}
                                </div>

                                <div className="mt-4">
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                                        {ach.name}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                        {ach.description}
                                    </p>
                                </div>

                                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs">
                                    <span className="inline-flex items-center gap-1 font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                                        +{ach.xp_reward} XP
                                    </span>
                                    <span className="uppercase tracking-wider text-gray-400 font-medium">
                                        {ach.category}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </DashboardLayout>
    );
}
